import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {forkJoin, of, Subscription} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import { User } from 'src/app/models/User';
import { AuthService } from 'src/app/shared/services/auth.service';
import { BroadcastsService } from 'src/app/shared/services/broadcasts.service';
import { ADMINISTRATOR, AGENCY_CLIENT, CLIENT, PARTNER_CLIENT } from 'src/app/shared/services/user.service';
import { urlValidatorService } from 'src/app/shared/validators/url.validator.service';

import { IMPORTING, SENT, SENT_WITH_ERRORS } from 'src/app/models/BroadcastStatus';
import { BlacklistValidatorService } from '../../../shared/validators/blacklist.validator.service';
import { Broadcast } from 'src/app/models/Broadcast';
import { LeadList } from 'src/app/models/LeadList';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import * as BroadcastType from 'src/app/models/BroadcastType';
import {blocksBroadcast, broadcastFunds} from '../../../models/BroadcastFunds';
import {WalletService} from '../../../shared/services/wallet.service';
import {DashboardService} from "../../../shared/services/dashboard.service";
import { ThemePalette } from '@angular/material/core';
import {
  PartialSendConfirmService,
  scheduleErrorMessage,
} from 'src/app/shared/components/partial-send-confirm-modal/partial-send-confirm.service';
import { toastOwnError } from 'src/app/shared/interceptors/http-error.interceptor';
import { WalletBalance, WalletBilling, WalletPrices } from 'src/app/models/Wallet';
import { BroadcastCostEstimate, estimateSmsCost } from './broadcast-cost';
import { DEFAULT_SEND_WINDOWS, minutesOf, spMinutesOfDay, SendWindows } from 'src/app/models/SendWindow';
import { SendWindowsService } from 'src/app/shared/services/send-windows.service';
import { SearchSelectOption } from 'src/app/shared/components/search-select/search-select.component';

type BroadcastStep = 1 | 2 | 3 | 4;

interface ProfileOption {
  id: string;
  name: string;
  selected: boolean;
}

@Component({
  selector: 'app-broadcast',
  templateUrl: './broadcast.component.html',
  styleUrls: ['./broadcast.component.scss']
})
export class BroadcastComponent implements OnInit, OnDestroy {

  title = 'Criar envio em massa';

  isSaving: boolean;

  /**
   * Só na edição. Sem isto, o broadcast existente aparece por um instante como
   * a tela de ESCOLHER o tipo de envio — `broadcastType` só é preenchido quando
   * a resposta chega, e o overlay branco é que escondia essa troca.
   */
  loadingBroadcast = false;

  /** O select de lista espera a sua chamada; o resto do formulário não. */
  loadingLists = false;
  startPosition = 0;
  endPosition = 0;

  color: ThemePalette = 'primary';


  tags = [
    { tag: 'meu_link', text: 'Meu link (encurtado)', broadcastType: ['all'] },
    { tag: 'campaign', text: 'Nome da automação', broadcastType: ['all'] },
    { tag: 'list', text: 'Nome da Lista', broadcastType: ['list'] },
    { tag: 'first_name', text: 'Primeiro nome do Lead', broadcastType: ['list', 'client'] },
    { tag: 'email', text: 'E-mail do Lead', broadcastType: ['client'] },
  ];

  changeDate: boolean;
  broadcast: Broadcast;
  broadcastId: string;
  broadcastForm: UntypedFormGroup;
  broadcastType: string;
  lists: LeadList[] = [];
  sendNow = false;
  removeContacts = false;
  subscriptions: Subscription[] = [];
  /** O POST/PUT de salvar, com a confirmação de saldo e o reenvio: um por vez, cancelado ao sair. */
  private saveSubscription: Subscription | null = null;
  user: User;

  profiles: ProfileOption[] = [
    { id: CLIENT, name: 'Cliente', selected: true },
    { id: AGENCY_CLIENT, name: 'Agência', selected: true },
    { id: PARTNER_CLIENT, name: 'Parceiro', selected: true },
  ];

  sendLeadNumber = false;

  /**
   * Passos do mockup aprovado (Destino, Mensagem, Agendamento, Revisar), no
   * padrão do stepper do modal de WhatsApp. Puramente visual: todos os
   * campos do formulário continuam montados (CSS `[hidden]`, nunca `*ngIf`)
   * em qualquer passo — tirar um controle do DOM com `*ngIf` o desregistra
   * do `broadcastForm`, e os testes (e o `save()`) dependem dele existir
   * sempre. Edição abre direto no passo 4 (decisão 1 do mockup).
   */
  currentStep: BroadcastStep = 1;
  maxStepReached: BroadcastStep = 1;
  /** Erro do passo atual ao tentar avançar — mesmas mensagens do `isValid()` de hoje. */
  stepError: string | null = null;

  readonly stepLabels = ['Destino', 'Mensagem', 'Agendamento', 'Revisar'];

  /** Para o resumo/custo estimado do passo 4 — não bloqueia nada, é só exibição. */
  walletBilling: WalletBilling | null = null;
  walletPrices: WalletPrices | null = null;
  walletBalance: WalletBalance | null = null;

  /** Janela de envio do SMS (GET /send-windows) — cai no default até responder. */
  windows: SendWindows = DEFAULT_SEND_WINDOWS;

  isDataMenorQueAtual(dataSelecionada: Date): boolean {
    const dataAtual = new Date();
    return dataSelecionada < dataAtual;
  }

  get tagsForType(): any[] {
    return this.tags.filter(t => t.broadcastType.includes(this.broadcastType) || t.broadcastType.includes('all'));
  }

  get numbersCount(): number {
    const numbers = this.broadcastForm.get('numbers').value;
    let count = numbers.split('\n').filter(n => n.trim() !== '').length;

    if (!numbers.includes('\n')) {
      count = numbers.split(',').filter(n => n.trim() !== '').length;
    }

    return count;
  }

  get isAdministrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  /** "Meu link" e "Enviar número do lead na URL?" só aparecem com {meu_link} na mensagem. */
  get messageHasMeuLink(): boolean {
    return (this.broadcastForm.get('message').value || '').includes('{meu_link}');
  }

  /** Quantidade estimada de leads, para o custo do passo 4. Não é o que o backend confere. */
  get estimatedQuantity(): number {
    if (this.broadcastType === 'number') {
      return this.numbersCount;
    }

    if (this.broadcastType === 'list') {
      const listId = this.broadcastForm.get('lead_list_id').value;
      if (listId === 'ALL') {
        return this.lists.reduce((sum, list) => sum + (list.leads_count || 0), 0);
      }
      const list = this.lists.find(l => l.id === listId);
      return list ? list.leads_count : 0;
    }

    // 'client': sem contagem por perfil disponível aqui — fica "calculado no envio".
    return 0;
  }

  /** Nome da lista escolhida, para o resumo do passo 4. */
  get selectedListLabel(): string {
    const listId = this.broadcastForm.get('lead_list_id').value;
    if (!listId) {
      return '—';
    }
    if (listId === 'ALL') {
      return 'Todas';
    }
    const list = this.lists.find(l => l.id === listId);
    return list ? list.name : '—';
  }

  get costEstimate(): BroadcastCostEstimate {
    return estimateSmsCost(this.estimatedQuantity, this.walletBilling?.sms, this.walletPrices, this.walletBalance);
  }

  /** Para o `app-search-select` do passo 1. "Todas" só entra fora da edição (igual à opção de hoje). */
  get listOptions(): SearchSelectOption[] {
    const options: SearchSelectOption[] = this.lists.map(list => ({
      value: list.id,
      label: list.name,
      hint: `${list.leads_count} leads`,
    }));

    if (!this.broadcast) {
      options.unshift({ value: 'ALL', label: 'Todas' });
    }

    return options;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private dashboardService: DashboardService,
    private walletService: WalletService,
    private toastService: ToastService,
    private broadcastsService: BroadcastsService,
    private listsService: ListsService,
    private blacklistValidator: BlacklistValidatorService,
    private partialSendConfirm: PartialSendConfirmService,
    private sendWindowsService: SendWindowsService,
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();

    this.broadcastForm = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      message: ['', Validators.required],
      numbers: [''],
      profile_id: [''],
      lead_list_id: [''],
      scheduled_date: [''],
      broadcast_type_id: [''],
      url: ['', urlValidatorService()],
    });

    this.subscriptions.push(
      this.route.params.subscribe(params => {
        if (params.id) {
          this.broadcastId = params.id;
          // Decisão 1 do mockup: editar abre direto no passo 4 (Revisar), com lápis
          // em cada seção para voltar a um passo anterior.
          this.currentStep = 4;
          this.maxStepReached = 4;
          this.findById();
        }
      })
    );

    // A trava de entrada é só da CRIAÇÃO (a mesma da lista). Na edição o envio já existe e o
    // cliente precisa poder reagendar ou corrigir sem saldo: o PUT vai e o backend decide, com o
    // modal de confirmação (T.10, rodada 3).
    if (!this.broadcastId) {
      this.blockCreationWithoutFunds();
    }

    this.loadWalletInfoForEstimate();

    this.subscriptions.push(
      this.sendWindowsService.get().subscribe(windows => this.windows = windows)
    );

    // "Meu link"/"Enviar número do lead" ficam com [hidden] quando a mensagem
    // não tem {meu_link} (m8-comum.md) — sem limpar o valor, se o usuário já
    // tinha preenchido a URL e depois tira a tag da mensagem, o passo 2 passa
    // a recusar com "preencheu Meu Link mas não incluiu {meu_link}" sem o
    // campo para ele corrigir (está escondido).
    this.subscriptions.push(
      this.broadcastForm.get('message').valueChanges.subscribe(() => {
        if (!this.messageHasMeuLink && this.broadcastForm.get('url').value) {
          this.broadcastForm.patchValue({ url: '' });
          this.sendLeadNumber = false;
        }
      })
    );
  }

  /**
   * Preço/saldo só para a ESTIMATIVA visual do passo 4 — separado de
   * `blockCreationWithoutFunds` (que decide se a tela bloqueia a criação) para não
   * mudar NENHUMA das condições/chamadas que esse método já faz (e os testes conferem).
   * `getPrices` é opcional por chamada (`?.()`): mocks antigos de `WalletService` nos
   * testes não o declaram, e sem a estimativa a tela cai em "calculado no envio".
   */
  private loadWalletInfoForEstimate(): void {
    this.subscriptions.push(
      forkJoin({
        billing: this.walletService.getBilling().pipe(catchError(() => of(null))),
        prices: (this.walletService.getPrices?.() ?? of(null)).pipe(catchError(() => of(null))),
        balance: this.walletService.getBalance().pipe(catchError(() => of(null))),
      }).subscribe(({ billing, prices, balance }) => {
        this.walletBilling = billing;
        this.walletPrices = prices;
        this.walletBalance = balance;
      })
    );
  }

  /** Só falta de dinheiro certa volta para a lista; sem resposta do billing a tela segue. */
  private blockCreationWithoutFunds(): void {
    this.subscriptions.push(
      forkJoin({
        // Sem os créditos a tela segue (o billing pode dizer que o canal é da carteira). Créditos
        // x contatos não é conferido aqui (T.10, 07/10): o backend decide e, faltando, pede
        // confirmação (422 `insufficient_balance_confirmation`, modal).
        credits: this.dashboardService.userCreditsInfo().pipe(catchError(() => of(null))),
        billing: this.walletService.getBilling().pipe(catchError(() => of(null))),
        balance: this.walletService.getBalance().pipe(catchError(() => of(null))),
      }).subscribe(({credits, billing, balance}) => {
        const funds = broadcastFunds('sms', credits ? credits.credits : null, billing, balance);

        if (blocksBroadcast(funds)) {
          this.toastService.error(funds === 'no-wallet-balance'
            ? 'Sem saldo na carteira para o envio em massa.'
            : 'Usuário não possui créditos.');
          this.router.navigate(['/broadcasts']);
        }
      })
    );
  }

  isImporting(broadcast: Broadcast): boolean {
    return broadcast.broadcast_status_id === IMPORTING;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.saveSubscription?.unsubscribe();
  }

  findById() {
    this.loadingBroadcast = true;
    this.subscriptions.push(
      this.broadcastsService.findById(this.broadcastId).subscribe({
        next: broadcast => {
          this.loadingBroadcast = false;

          if (broadcast.broadcast_status_id === SENT || broadcast.broadcast_status_id === SENT_WITH_ERRORS) {
            this.router.navigate(['/broadcasts/view', broadcast.id]);
          }

          const broadcastType = BroadcastType.getBroadcastTypeName(broadcast.broadcast_type_id);
          this.changeBroadcastType(broadcastType);
          this.broadcast = broadcast;
          this.sendLeadNumber = broadcast.send_lead_number;
          this.broadcastForm.patchValue(broadcast);
        },
        error: () => { this.loadingBroadcast = false; },
      })
    );
  }

  loadLists(): void {
    if (this.broadcastType === 'list' && this.lists.length === 0) {
      this.loadingLists = true;
      this.subscriptions.push(
        this.listsService.listAll().subscribe({
          next: lists => {
            this.loadingLists = false;
            this.lists = lists;
          },
          error: () => { this.loadingLists = false; },
        })
      );
    }
  }

  changeBroadcastType(broadcastType: string): void {
    this.title = 'Enviar broadcast para ';
    this.broadcastType = broadcastType;

    if (broadcastType === 'list') {
      this.title += 'lista';
    }
    else if (broadcastType === 'number') {
      this.title += 'números';
    }
    else if (broadcastType === 'client') {
      this.title += 'clientes da SMSFunnel';
    }

    this.broadcastForm.patchValue({
      numbers: '',
      lead_list_id: '',
      broadcast_type_id: BroadcastType.getBroadcastTypeId(broadcastType)
    });

    this.loadLists();
  }

  addTag(tag: string): void {
    let message = this.broadcastForm.get('message').value || '';

    const size = message.length + ` {${tag}} `.length;
    if (size < 150) {
      const beforeText = message.substring(0, this.startPosition);
      const afterText = message.substring(this.endPosition, message.length);

      message = beforeText + ` {${tag}} ` + afterText;

      this.broadcastForm.patchValue({ message });
    } else {
      this.toastService.error('Não há espaço suficiente para inserir esta TAG');
    }


  }

  setSendNow(value: boolean): void {
    this.sendNow = value;
    this.syncScheduleControl();
  }

  /**
   * Em "Enviar agora" a data não vai no POST/PUT (`save()` manda vazio), então o picker
   * fica desabilitado e fora da validade do formulário: uma hora bloqueada digitada antes
   * de trocar para "Enviar agora" não pode deixar o botão final desabilitado sem nada na
   * tela (retorno do review r2, 29/10 — mesmo padrão do `syncScheduleControls` do editar
   * de Ligação). `save()` lê com `getRawValue()`, então nada muda no payload.
   */
  private syncScheduleControl(): void {
    const control = this.broadcastForm.get('scheduled_date');
    if (this.sendNow) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  toggleChangeDate(): void {
    this.changeDate = true;
    this.broadcastForm.patchValue({ scheduled_date: null });
  }

  // --- Stepper (M8 / CU-17tm466nzee) ----------------------------------------
  // Só UI: `isValid()` continua sendo a trava real no `save()`, sem mudar nada nela.

  canGoToStep(step: BroadcastStep): boolean {
    return step <= this.maxStepReached;
  }

  goToStep(step: BroadcastStep): void {
    if (this.canGoToStep(step)) {
      this.currentStep = step;
      this.stepError = null;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep = (this.currentStep - 1) as BroadcastStep;
      this.stepError = null;
    }
  }

  nextStep(): void {
    const error = this.validateStep(this.currentStep);
    if (error) {
      this.stepError = error;
      return;
    }

    this.stepError = null;
    if (this.currentStep < 4) {
      this.currentStep = (this.currentStep + 1) as BroadcastStep;
      this.maxStepReached = Math.max(this.maxStepReached, this.currentStep) as BroadcastStep;
    }
  }

  /**
   * Mesmas mensagens de `isValid()` (texto de hoje), fatiadas por passo para erro
   * inline. Duplica a checagem de propósito: `isValid()` continua sendo quem decide
   * de verdade no `save()` — este método só melhora a UX de apontar o passo errado.
   */
  private validateStep(step: BroadcastStep): string | null {
    const message = this.broadcastForm.get('message').value || '';
    const url = this.broadcastForm.get('url').value;

    if (step === 1) {
      if (!this.broadcastForm.get('name').value) {
        return 'Preencha o nome do envio';
      }
      if (this.broadcastType === 'list' && !this.broadcastForm.get('lead_list_id').value) {
        return 'Selecione uma lista!';
      }
      if (this.broadcastType === 'number' && this.numbersCount === 0) {
        return 'Cole pelo menos um número (DDD + número, um por linha ou separados por vírgula).';
      }
      if (this.broadcastType === 'client' && !this.profiles.some(p => p.selected)) {
        return 'Você precisa selecionar ao menos um dos perfis de clientes';
      }
      return null;
    }

    if (step === 2) {
      if (!message) {
        return 'Preencha a sua mensagem';
      }
      if (message.length > 150) {
        return 'O texto deve ter apenas 150 caracteres';
      }
      if (url && !message.includes('{meu_link}')) {
        return 'Você preencheu o campo "Meu Link", mas não incluiu a variável {meu_link} na mensagem.';
      }
      if (message.includes('{meu_link}')) {
        if (!url) {
          return 'Você está utilizando a TAG {meu_link} mas não preencheu o campo "Meu Link"!';
        }
        const size = message.length + 15;
        if (size > 150) {
          return `Ao incluir um link, a mensagem ficará com ${size} caracteres.`;
        }
      }
      if (this.broadcastForm.get('url').errors?.invalidUrl) {
        return 'URL Inválida! Exemplo: https://exemplo.com';
      }
      const blacklistError = this.blacklistValidator.validate(message);
      if (blacklistError) {
        return blacklistError.message;
      }
      if (this.sendLeadNumber && !url) {
        return 'Para enviar o número dos Leads você precisa preencher o campo Meu Link';
      }
      return null;
    }

    if (step === 3) {
      if (!this.sendNow) {
        // A mensagem do picker (hora bloqueada, já passou, hora pela metade) é mais
        // precisa que o genérico "Escolha a data e a hora" — e é a mesma que está embaixo do campo.
        const pickerError = this.broadcastForm.get('scheduled_date').errors?.sendWindow;
        if (pickerError) {
          return pickerError;
        }
        const error = this.scheduleDateError(this.broadcastForm.get('scheduled_date').value);
        if (error) {
          return error;
        }
      }
      return null;
    }

    return null;
  }

  /**
   * Ajuste do Bruno (14/10, CU-17tm466nzee): dava para avançar/salvar com "Agendar para
   * data e hora" sem escolher data nenhuma — `scheduled_date` vazio vira `Invalid Date`,
   * a comparação `<` com `new Date()` dá sempre `false` (NaN nunca é menor que nada) e
   * passava. Usado tanto pelo erro inline do passo 3 (`validateStep`) quanto pelo `save()`
   * (`isValid()`), para a mesma regra valer na criação, na edição (que abre direto no
   * passo 4) e nos 3 tipos de destino (lista/números/clientes).
   */
  private scheduleDateError(scheduledDate: unknown): string | null {
    if (!scheduledDate || isNaN(new Date(scheduledDate as string).getTime())) {
      return 'Escolha a data e a hora do agendamento.';
    }
    if (new Date(scheduledDate as string) < new Date()) {
      return 'A data do agendamento deve ser uma data futura!';
    }
    // Inclusivo no fim (<=), não `isInWindow` (exclusivo): o backend aceita 22:00:00 em
    // ponto (`SmsSendWindow`, precisão de segundo) — um broadcast editado que já estava
    // agendado bem no limite não pode ficar bloqueado só pelo front. O picker já evita
    // esse exato minuto na hora de ESCOLHER uma data nova (de propósito, ver m8-coord.md).
    const minute = spMinutesOfDay(new Date(scheduledDate as string));
    if (minute < minutesOf(this.windows.sms.start) || minute > minutesOf(this.windows.sms.stop)) {
      return `O agendamento precisa estar dentro do horário de envio (${this.windows.sms.start}–${this.windows.sms.stop}, horário de Brasília).`;
    }
    return null;
  }

  isValid(): boolean {
    const broadcast = this.broadcastForm.getRawValue() as Broadcast;

    if (!this.sendNow) {
      const error = this.scheduleDateError(broadcast.scheduled_date);
      if (error) {
        this.toastService.error(error);
        return false;
      }
    }

    if (this.broadcastType === 'list') {

      if (!broadcast.lead_list_id) {
        this.toastService.error('Selecione uma lista!');
        return false;
      }

      const leadList = this.lists.find(l => l.id == broadcast.lead_list_id);

      if (!leadList) {
        this.toastService.error('Selecione uma lista!');
        return false;
      }
    }

    if (broadcast.url && !broadcast.message.includes('{meu_link}')) {
      this.toastService.error('Você preencheu o campo "Meu Link", mas não incluiu a variável {meu_link} na mensagem.');
      return false;
    }

    if (broadcast.message.includes('{meu_link}')) {
      if (!broadcast.url) {
        this.toastService.error('Você está utilizando a TAG {meu_link} mas não preencheu o campo "Meu Link"!');
        return false;
      }

      const size = broadcast.message.length + 15;
      if (size > 150) {
        this.toastService.error(`Ao incluir um link, a mensagem ficará com ${size} caracteres.`);
        return false;
      }
    }

    const error = this.blacklistValidator.validate(broadcast.message);
    if (error) {
      this.toastService.error(error.message);
      return false;
    }

    if (this.broadcastType === 'client') {
      const selectedProfiles = this.profiles.filter(p => p.selected);
      if (selectedProfiles.length === 0) {
        this.toastService.error('Você precisa selecionar ao menos um dos perfis de clientes');
        return false;
      }
    }

    if (this.sendLeadNumber && !broadcast.url) {
      this.toastService.error('Para enviar o número dos Leads você precisa preencher o campo Meu Link');
      return false;
    }

    return true;
  }

  save(): void {
    if (this.isSaving) {
      return;
    }

    const broadcast = this.broadcastForm.getRawValue() as Broadcast;
    if(this.sendNow){
      broadcast.scheduled_date = '';
    } else if (broadcast.scheduled_date && !(broadcast.scheduled_date instanceof Date)) {
      // Review do MR !558 (ações 2 e 8): editar SEM tocar no picker deixa no controle a string
      // crua que a API devolveu em `findById()` (ISO UTC com "Z", cast `datetime` do Eloquent).
      // Sem isto ela ia no PUT tal e qual, sem passar pelo `Date.prototype.toJSON` global
      // (app.module.ts) que todo Date escolhido no picker passa — payload com formato diferente
      // conforme o usuário mexeu ou não na data. O "Z" deixa o instante inequívoco.
      broadcast.scheduled_date = new Date(broadcast.scheduled_date);
    }

    // M8 / CU-17tm466nzee: o app-window-datetime-picker já entrega um Date com o
    // instante absoluto certo (spWallTimeToDate, independente do fuso do
    // navegador) — o Date.prototype.toJSON global (app.module.ts) serializa
    // isso como a hora de parede de São Paulo. A conversão dupla que existia
    // aqui (toLocaleString de novo antes do toJSON) só dava a data certa para
    // quem já estava no fuso de Brasília; em qualquer outro fuso deslocava a
    // hora duas vezes.

    broadcast.remove_contacts = this.removeContacts;
    broadcast.send_lead_number = this.sendLeadNumber;

    if (!this.isValid()) {
      return;
    }

    if (this.broadcastType === 'client') {
      broadcast.profiles = this.profiles.filter(p => p.selected).map(p => p.id);
    }

    const isUpdate = !!broadcast.id;
    if (isUpdate && broadcast.lead_list_id) {
      delete broadcast.remove_contacts;
    }

    const successMessage = isUpdate
      ? 'Broadcast alterado com sucesso!'
      : broadcast.scheduled_date ? 'Broadcast agendado com sucesso' : 'Broadcast cadastrado com sucesso!';
    const errorMessage = isUpdate ? 'Erro ao alterar broadcast!' : 'Erro ao enviar broadcast!';

    // Saldo abaixo do custo: o backend pede confirmação (422) e o modal decide se reenvia com
    // `confirm_partial` (T.10) — nos dois ramos, criar e editar.
    this.isSaving = true;
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = this.partialSendConfirm.schedule(confirmPartial => isUpdate
      ? this.broadcastsService.update(broadcast, confirmPartial)
      : this.broadcastsService.send(broadcast, confirmPartial)
    ).pipe(
      finalize(() => this.isSaving = false)
    ).subscribe({
      next: () => {
        this.toastService.success(successMessage);
        this.router.navigate(['/broadcasts']);
      },
      error: err => toastOwnError(this.toastService, scheduleErrorMessage(err, errorMessage)),
    });
  }

  setPosition(e: any) {
    this.startPosition = e.target.selectionStart;
    this.endPosition = e.target.selectionEnd;
  }
}
